import React, { useMemo, useState, useEffect } from 'react';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import {
  detectTable, detectTables, mapColumns, extractRows, extractRowsFromTables, COLUMN_ROLES,
} from '../../utils/excelImport';

const ROLE_LABELS = {
  ignore:          'Ignore',
  date:            'Date',
  description:     'Description',
  amount:          'Amount',
  currency:        'Currency',
  foreignAmount:   'Foreign amount',
  foreignCurrency: 'Foreign currency',
  debit:           'Debit',
  credit:          'Credit',
  notes:           'Notes',
};

/** Preview + confirmation step shown between file upload and the categorize step. */
export default function ExcelMappingStep({ sheets, mainCurrency = 'ILS', onCancel, onConfirm }) {
  // Pick the first sheet that has a detectable table, else the first sheet.
  const initialIdx = useMemo(() => {
    const i = sheets.findIndex(s => detectTables(s).length > 0);
    return i >= 0 ? i : 0;
  }, [sheets]);

  const [sheetIdx, setSheetIdx] = useState(initialIdx);
  const sheet = sheets[sheetIdx];
  const autoTables = useMemo(() => detectTables(sheet), [sheet]);
  const autoDetect = useMemo(() => detectTable(sheet), [sheet]);
  const [headerRow, setHeaderRow] = useState(autoDetect ? autoDetect.headerRow : 0);
  const [roles, setRoles] = useState(() => {
    if (!autoDetect) return (sheet.aoa[0] || []).map(() => 'ignore');
    return mapColumns(sheet.aoa[autoDetect.headerRow], { mainCurrency }).roles;
  });

  // Reset when sheet changes
  useEffect(() => {
    const det = detectTable(sheet);
    const newHeader = det ? det.headerRow : 0;
    setHeaderRow(newHeader);
    setRoles(mapColumns(sheet.aoa[newHeader] || [], { mainCurrency }).roles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetIdx]);

  // Compute detection from current headerRow (re-walk the data range)
  const detection = useMemo(() => {
    if (headerRow < 0 || headerRow >= sheet.aoa.length) return null;
    // Cheat: re-run detectTable, but if the user picked a different header row,
    // we still need a data range. Fall back to "everything below until 2 blanks".
    const auto = detectTable(sheet);
    if (auto && auto.headerRow === headerRow) return auto;
    // Build a manual detection starting at headerRow + 1
    let endRow = headerRow;
    let blankStreak = 0;
    for (let r = headerRow + 1; r < sheet.aoa.length; r++) {
      const row = sheet.aoa[r] || [];
      const blank = !row || row.every(v => v == null || (typeof v === 'string' && v.trim() === ''));
      if (blank) { blankStreak++; if (blankStreak >= 2) break; continue; }
      blankStreak = 0;
      endRow = r;
    }
    return { headerRow, startRow: headerRow + 1, endRow, warnings: [] };
  }, [sheet, headerRow]);

  const isAutoHeader = Boolean(autoDetect && detection && headerRow === autoDetect.headerRow);
  const importingAllDetectedTables = isAutoHeader && autoTables.length > 1;

  const parsed = useMemo(() => {
    if (!detection) return [];
    if (importingAllDetectedTables) {
      return extractRowsFromTables(sheet, autoTables, detection, { roles }, { mainCurrency });
    }
    return extractRows(sheet, detection, { roles }, { mainCurrency });
  }, [sheet, autoTables, detection, roles, mainCurrency, importingAllDetectedTables]);

  const previewRows = parsed.slice(0, 5);
  const headerCells = sheet.aoa[headerRow] || [];
  const rowCount = importingAllDetectedTables
    ? autoTables.reduce((sum, table) => sum + table.endRow - table.startRow + 1, 0)
    : detection ? detection.endRow - detection.startRow + 1 : 0;
  const parsedCount = parsed.length;
  const skippedCount = rowCount - parsedCount;

  function updateRole(colIdx, role) {
    setRoles(prev => prev.map((r, i) => i === colIdx ? role : r));
  }

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, flex: 1, overflow: 'auto' }}>
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        Detected a transaction table. Confirm the column mapping below, then continue to categorize.
      </p>

      {sheets.length > 1 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span className="muted">Sheet:</span>
          <select value={sheetIdx} onChange={e => setSheetIdx(parseInt(e.target.value, 10))}
                  style={{ fontSize: 13, padding: '3px 8px' }}>
            {sheets.map((s, i) => (
              <option key={i} value={i}>{s.name} ({s.aoa.length} rows)</option>
            ))}
          </select>
        </label>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <span className="muted">Header row:</span>
        <input
          type="number" min="0" max={Math.max(sheet.aoa.length - 1, 0)}
          value={headerRow}
          onChange={e => {
            const r = Math.max(0, Math.min(sheet.aoa.length - 1, parseInt(e.target.value || '0', 10)));
            setHeaderRow(r);
            setRoles(mapColumns(sheet.aoa[r] || [], { mainCurrency }).roles);
          }}
          style={{ width: 70, padding: '3px 6px', fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
        <span className="muted" style={{ fontSize: 12 }}>(row index, 0-based)</span>
      </div>

      {importingAllDetectedTables && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'var(--warning-bg, #fef3c7)', borderRadius: 6, fontSize: 12 }}>
          <AlertTriangle size={14} />
          Multiple tables detected on this sheet — all detected tables will be imported (each auto-mapped from its own header row).
        </div>
      )}

      {/* Column mapping grid */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              {headerCells.map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border)', minWidth: 120 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{h == null ? <em className="muted">(empty)</em> : String(h)}</div>
                  <select value={roles[i] || 'ignore'} onChange={e => updateRole(i, e.target.value)}
                          style={{ fontSize: 11, padding: '2px 4px', width: '100%' }}>
                    {COLUMN_ROLES.map(role => (
                      <option key={role} value={role}>{ROLE_LABELS[role] || role}</option>
                    ))}
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(detection ? sheet.aoa.slice(detection.startRow, Math.min(detection.startRow + 5, detection.endRow + 1)) : []).map((row, ri) => (
              <tr key={ri} style={{ borderBottom: '1px solid var(--border)' }}>
                {headerCells.map((_, ci) => {
                  const v = row[ci];
                  const display = v instanceof Date ? v.toISOString().slice(0, 10) : (v == null ? '' : String(v));
                  return <td key={ci} style={{ padding: '5px 10px', fontFamily: 'var(--font-mono)', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Parsed preview */}
      <div>
        <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
          Parsed preview ({parsedCount} of {rowCount} rows)
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
          {previewRows.length === 0 ? (
            <div className="muted" style={{ padding: 12, fontSize: 12, fontStyle: 'italic' }}>
              No rows parsed — check column mapping above.
            </div>
          ) : previewRows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 10px', borderBottom: i < previewRows.length-1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
              <span className="mono muted" style={{ minWidth: 80 }}>{r.date || <em>bad date</em>}</span>
              <span className="mono" style={{ minWidth: 80, color: r.type === 'income' ? 'var(--income)' : 'var(--expense)' }}>
                {r.type === 'income' ? '+' : '−'}{r.currency} {r.amount}
              </span>
              <span style={{ flex: 1, direction: 'rtl', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'auto', paddingTop: 8 }}>
        <span className="muted" style={{ fontSize: 12, flex: 1 }}>
          {parsedCount > 0
            ? <><strong style={{ color: 'var(--text-primary)' }}>{parsedCount}</strong> rows ready{skippedCount > 0 ? ` · ${skippedCount} skipped` : ''}</>
            : 'No rows parsed'}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button
          className="btn btn-primary"
          disabled={parsedCount === 0}
          onClick={() => onConfirm(parsed.filter(r => !r.error))}
        >
          Continue <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
